import { useCallback, useEffect, useState } from 'react';
import { useI18n, type CopyKey } from '../i18n';
import Install from '../install/Install';
import { enablePush } from '../push';
import { reachFacts } from './device';
import { canSubscribeHere, reachPlan, UNKNOWN_DEVICE, type ReachPlan } from './reach';
import { handoffUrl } from './handoff';
import { encodeQr, qrPath, type QrMatrix } from './qr';

/**
 * Step 2b of the first run: making sure a question can actually arrive. F-141.
 *
 * Leo, on putting this behind the same gate as the schedule: *"yes part of the gate but we must
 * make sure that we handle device specificity as part of onboarding."* A schedule with nowhere to
 * deliver on it is half a setting, and the half that is missing is the one nobody notices until a
 * week of silence.
 *
 * **Every decision on this screen is next door in `reach.ts`**, which is React-free and asserted
 * from plain Node over every device this app can be opened on. What is left here is the wiring and
 * the words — for `gate.ts`'s reason, and with one of its own: the answers are promises about
 * whether a question will arrive, and a wrong one does not look wrong. It looks like a button.
 *
 * ## What this screen will not do
 *
 * - **It will not offer a control the platform does not have.** On iOS in a tab there is no
 *   `Notification` to ask, so the ask is the install and the copy says why.
 * - **It will not ask twice.** The browser's prompt is one-shot per origin: a second request after
 *   a dismissal resolves `denied` without showing anybody anything. So the explanation comes
 *   *before* the button, and a refusal leads to the browser's own settings rather than to a retry.
 * - **It will not end without saying how the learner is reached.** `plan.floor` is rendered every
 *   time, above the button out, and it follows a stored subscription rather than a granted
 *   permission — see `reach.ts`, which explains why those are not the same thing.
 * - **It will not be impassable.** The button out is always there. On a device that physically
 *   cannot grant push that is the whole design: email is the floor, F-122 built it, and the screen
 *   says so in as many words before the learner presses on.
 */

/**
 * How long to wait for the device facts before giving up on them.
 *
 * **`get` in `api.ts` has no timeout**, so `reachFacts` can await a request that never settles —
 * and a screen with no plan has no continue button on it, on a step every other route redirects
 * back to. Without this the learner sits on one muted line for ever.
 *
 * Six seconds because the read is two round trips on a connection that is working and this is the
 * point at which a learner on a bad one has decided the screen is stuck. Whatever arrives late is
 * still used: the race resolves the fallback, the real read carries on, and `look` sets whichever
 * plan it produces when it lands.
 */
const FACTS_MS = 6_000;

/** Which copy explains a refusal from `enablePush`. */
const PROBLEM_COPY: Record<string, CopyKey> = {
  not_configured: 'push.error.notConfigured',
  failed: 'reach.problem',
};

/**
 * The address, as a picture a phone's camera can read.
 *
 * Takes the matrix rather than the URL, so the caller — which has to choose its copy from whether
 * there is a picture — decides once. The address is rendered as text underneath either way, which
 * is a worse control and not a broken screen.
 */
function QrCode({ matrix, label }: { matrix: QrMatrix; label: string }) {
  const { path, viewBox } = qrPath(matrix);
  return (
    <svg className="reach-qr" viewBox={viewBox} role="img" aria-label={label}>
      {/* The light ground is drawn rather than inherited: a QR on a dark page is a QR that does not
          scan, and the viewer's theme is not ours to predict. */}
      <rect width="100%" height="100%" fill="#ffffff" />
      <path d={path} fill="#000000" shapeRendering="crispEdges" />
    </svg>
  );
}

export default function Reachable({
  /**
   * Called when the learner presses on, and never by anything else.
   *
   * **The step does not finish itself**, which is the whole of the fix for what Leo hit on the
   * screen before this one: a setting that saved and, by saving, navigated. Granting permission
   * here changes what this screen says and what the button says; it does not press the button.
   */
  onDone,
}: {
  onDone: () => void;
}) {
  const { t } = useI18n();
  const [plan, setPlan] = useState<ReachPlan>();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<CopyKey>();

  /*
   * Read from the browser and from the account, and re-read after the learner acts.
   *
   * Not a subscription to anything: the facts move only when this screen causes them to, and a
   * poll would be asking a browser for a permission state once a second for the length of a step
   * somebody may spend two minutes reading.
   */
  const look = useCallback(async () => {
    /*
     * **This always ends with a plan, and that is the property rather than the code.**
     *
     * Two ways it would otherwise not. A throw — `api.ts` answers an `ApiError` for a failed
     * request but not for a failure anywhere else on the path, which is why `FirstRunProvider`
     * adds a `.catch` to its equivalent read — and a request that never settles, which `get` has
     * no timeout to stop. Either leaves `plan` undefined, and while it is undefined this screen
     * has no continue button while `gateIsClosed('reach')` redirects every other route back to it.
     *
     * So both are bounded and both land on `UNKNOWN_DEVICE`: nothing offered, nothing promised,
     * and email named as what carries the questions — which is true whatever the device turns out
     * to be. A late answer is not thrown away; it replaces the fallback when it arrives.
     */
    const timeout = new Promise<'late'>((resolve) => setTimeout(() => resolve('late'), FACTS_MS));
    const facts = reachFacts().catch(() => 'failed' as const);
    const first = await Promise.race([facts, timeout]);
    setPlan(first === 'late' || first === 'failed' ? UNKNOWN_DEVICE : reachPlan(first));
    if (first === 'late') {
      const eventual = await facts;
      if (eventual !== 'failed') setPlan(reachPlan(eventual));
    }
  }, []);

  useEffect(() => {
    void look();
  }, [look]);

  /*
   * Not `async` before `enablePush`, and that is load-bearing.
   *
   * `enablePush` calls `Notification.requestPermission()` before its first `await`, because Safari
   * only honours a permission request still inside the user gesture that triggered it. Anything
   * awaited here first — a fact read, a check — ends that gesture, and the prompt is refused
   * without ever being shown. `Push.tsx` carries the same note for the same call.
   */
  async function allow() {
    setBusy(true);
    setProblem(undefined);
    const result = await enablePush();
    setBusy(false);

    if (!result.ok) {
      /*
       * A denial is not an error and gets no red text. The learner was asked and said no, which is
       * a supported answer — re-reading the facts turns the screen into the one that says how email
       * carries the questions instead, which is the honest thing to show somebody who just declined.
       */
      if (result.reason !== 'denied') setProblem(PROBLEM_COPY[result.reason] ?? 'reach.problem');
      await look();
      return;
    }
    await look();
  }

  if (!plan) return <p className="muted">{t('reach.checking')}</p>;

  const url = handoffUrl(window.location.origin);
  /*
   * Encoded once, here, because two things depend on whether it worked: the picture, and the
   * sentence above it. Computing it inside `QrCode` would leave the copy claiming a camera can be
   * pointed at something the component then declined to draw.
   */
  const matrix = url ? encodeQr(url) : undefined;

  return (
    <>
      <section className="panel">
        <h2>{t('reach.title')}</h2>
        <p className="muted">{t('reach.lede')}</p>

        {canSubscribeHere(plan) && (
          <div className="form">
            <div className="field">
              <strong>{t('reach.permission.title')}</strong>
              {/*
                Before the button, never after. The browser asks once per site for the life of the
                site, so this paragraph is the only chance anybody gets to know what the box is.
              */}
              <small>{t('reach.permission.body')}</small>
            </div>
            <div className="actions">
              <button
                type="button"
                className="button primary"
                onClick={() => void allow()}
                disabled={busy}
              >
                {busy ? t('reach.permission.working') : t('reach.permission.button')}
              </button>
            </div>
          </div>
        )}

        {plan.ask === 'install' && <p className="muted">{t('reach.install.body')}</p>}
        {plan.ask === 'dock' && <p className="muted">{t('reach.dock.body')}</p>}
        {plan.ask === 'settings' && (
          <p className="problem" role="alert">
            {t('reach.settings.body')}
          </p>
        )}
        {/*
          `none` covers two opposite states and they must not share a sentence: a device that is
          already subscribed, and a browser that can never be. `canSubscribeHere` is false for both,
          so the subscription is what tells them apart.
        */}
        {plan.ask === 'none' && plan.floor === 'push' && (
          <div className="field">
            <strong>{t('reach.on.title')}</strong>
            <small>{t('reach.on.body')}</small>
          </div>
        )}
        {plan.ask === 'none' && plan.floor === 'email' && (
          <p className="muted">{t('reach.unsupported.body')}</p>
        )}

        {/*
          The install funnel, unchanged and not re-implemented (F-121, F-123).

          It knows the three routes, draws the Share glyph rather than naming it, hands off to
          Safari from another iOS browser, and counts the offer for the funnel that decides whether
          this product ever needs a native app. A second copy of any of that here would be a second
          place the iOS instructions are written, and the two would disagree the first time one
          changed.

          Rendered inside this panel rather than in one of its own, which is `Push.tsx`'s
          arrangement and is not only consistency: `Install` re-reads the route at render time and
          answers `null` once the app is installed — so a panel wrapped around it would be an empty
          bordered box on the screen of somebody who installed nabu in another window while this was
          open.
        */}
        {plan.install && <Install compact />}

        {problem && (
          <p className="problem" role="alert">
            {t(problem)}
          </p>
        )}

        {/*
          What will happen, said every time and never omitted.

          This is the sentence the whole step exists to be able to write: it names the channel a
          question will arrive on if the learner leaves it exactly as it stands. It follows a stored
          subscription rather than a granted permission, for the reason `reach.ts` gives — a browser
          can hold permission with no row behind it, and that state looks like success and is
          silence.
        */}
        <p className="plain">
          <span>{t(plan.floor === 'push' ? 'reach.floor.push' : 'reach.floor.email')}</span>
        </p>

        {/*
          The way on, and it is always available.

          **On iOS in a tab there is no permission to grant**, so a step that required one would be
          unfinishable on the platform this product's market is mostly on. What makes that honest
          rather than a hole is the line above: the learner is told what will carry their questions
          before they press, and the button says which of the two they are choosing.

          **It is the filled button only when nothing else on the screen is.** Where the browser can
          still be asked, the ask is the thing worth doing and this is the way past it; two filled
          buttons would be the screen saying both are the point, which is the failure the palette
          note at the top of `styles.css` is written against — one level up from colour, since these
          are both teal.
        */}
        <div className="actions">
          <button
            type="button"
            className={canSubscribeHere(plan) ? 'button' : 'button primary'}
            onClick={onDone}
          >
            {t(plan.floor === 'push' ? 'reach.done.push' : 'reach.done.email')}
          </button>
        </div>
      </section>

      {plan.qr && (
        <section className="panel">
          <h2>{t('reach.qr.title')}</h2>
          {/*
            Two sentences, because one of them is about a picture that may not be drawn. `encodeQr`
            refuses an address longer than its five versions hold, and `handoffUrl` refuses a plain
            http origin — neither happens in production, and *"point your phone's camera at this"*
            above nothing at all is the kind of copy that makes a learner think the page is broken
            rather than that they should type the address.
          */}
          <p className="muted">{t(matrix ? 'reach.qr.body' : 'reach.qr.body.typed')}</p>
          {/*
            The URL comes from `handoff.ts` and is built nowhere else. Today it is the plain
            address; the signed hand-off that would sign the phone in on arrival is a credential in
            a link and is being designed separately, with its own review. That file is the seam.
          */}
          {matrix && <QrCode matrix={matrix} label={t('reach.qr.alt')} />}
          <p className="muted small">
            {t('reach.qr.fallback')} <strong>{url ?? window.location.origin}</strong>
          </p>
        </section>
      )}
    </>
  );
}
